/**
 * SchedulingStrip (DESIGN.md T14, compact form for the event editor): one row per attendee/room with its busy
 * blocks for the day, the working-hours span and the proposed meeting slot. Read-only; the editor changes the slot.
 */
import { cn } from '../lib/utils';
import { addDays, startOfDay } from './model';

export type BusyKind = 'busy' | 'tentative' | 'oof' | 'workingElsewhere' | 'free';

export interface SchedulingRow {
  id: string;
  label: string;
  /** Second line (address, "Room · 8 seats"). */
  detail?: string | undefined;
  busy: readonly { start: Date; end: Date; kind: BusyKind }[];
  /** Free/busy could not be read: shown hatched with this text. */
  unavailable?: string | undefined;
}

export interface SchedulingStripProps {
  day: Date;
  rows: readonly SchedulingRow[];
  slot: { start: Date; end: Date };
  /** First and last hour shown (default 7–20). */
  fromHour?: number;
  toHour?: number;
  formatHour: (hour: number) => string;
  /** Accessible summary of a row ("Bob: busy 10:00–11:00"). */
  rowSummary: (row: SchedulingRow) => string;
  labels: { title: string; legendBusy: string; legendTentative: string; legendAway: string; slot: string };
  loading?: boolean;
  className?: string;
}

const KIND_CLASS: Record<BusyKind, string> = {
  busy: 'bg-primary/70',
  tentative: 'bg-primary/30',
  oof: 'bg-chart-4/70',
  workingElsewhere: 'bg-chart-2/50',
  free: 'bg-transparent',
};

export function SchedulingStrip({ day, rows, slot, fromHour = 7, toHour = 20, formatHour, rowSummary, labels, loading, className }: SchedulingStripProps) {
  const start = startOfDay(day);
  start.setHours(fromHour);
  const end = startOfDay(day);
  end.setHours(toHour);
  const span = end.getTime() - start.getTime();
  const position = (from: Date, to: Date) => {
    const left = Math.max(0, (from.getTime() - start.getTime()) / span);
    const right = Math.min(1, (to.getTime() - start.getTime()) / span);
    return right > left ? { left: `${left * 100}%`, width: `${(right - left) * 100}%` } : undefined;
  };
  const hours = Array.from({ length: toHour - fromHour + 1 }, (_, i) => fromHour + i);
  const slotBox = slot.end > start && slot.start < addDays(startOfDay(day), 1) ? position(slot.start, slot.end) : undefined;

  return (
    <section aria-label={labels.title} aria-busy={loading} className={cn('flex flex-col gap-1 rounded-md border p-2 text-xs', className)}>
      <div className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[minmax(6rem,10rem)_1fr] gap-x-2">
        <span className="font-medium">{labels.title}</span>
        <div className="relative h-4">
          {/* Every second hour; every fourth on phones; none at the right edge (no overflow). */}
          {hours
            .filter((hour) => hour < toHour - 1 && (hour - fromHour) % 2 === 0)
            .map((hour) => (
              <span
                key={hour}
                className={cn('absolute whitespace-nowrap text-muted-foreground', hour === fromHour ? '' : '-translate-x-1/2', (hour - fromHour) % 4 !== 0 && 'max-sm:hidden')}
                style={{ left: `${((hour - fromHour) / (toHour - fromHour)) * 100}%` }}
              >
                {formatHour(hour)}
              </span>
            ))}
        </div>
      </div>
      <ul className="flex flex-col gap-1">
        {rows.map((row) => (
          <li key={row.id} tabIndex={0} className="grid grid-cols-[4.5rem_1fr] sm:grid-cols-[minmax(6rem,10rem)_1fr] items-center gap-x-2 rounded-sm focus-visible:outline focus-visible:outline-ring" aria-label={rowSummary(row)}>
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{row.label}</span>
              {row.detail && <span className="truncate text-muted-foreground">{row.detail}</span>}
            </span>
            <div aria-hidden className={cn('relative h-6 overflow-hidden rounded-sm border bg-muted/30', row.unavailable && 'bg-muted')}>
              {hours.slice(0, -1).flatMap(hour => [hour, hour + .5]).slice(1).map((hour) => (
                <span key={hour} className="absolute inset-y-0 border-l border-border/60" style={{ left: `${((hour - fromHour) / (toHour - fromHour)) * 100}%` }} />
              ))}
              {row.busy.map((block) => {
                const box = position(block.start, block.end);
                return box && <span key={`${block.start.getTime()}-${block.kind}`} className={cn('absolute inset-y-0.5 rounded-sm', KIND_CLASS[block.kind])} style={box} />;
              })}
              {row.unavailable && <span className="absolute inset-0 flex items-center px-1 text-muted-foreground">{row.unavailable}</span>}
              {slotBox && <span className="absolute inset-y-0 border-x-2 border-ring bg-ring/10" style={slotBox} />}
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3 pt-1 text-muted-foreground" aria-hidden>
        <span className="flex items-center gap-1"><span className={cn('size-3 rounded-sm', KIND_CLASS.busy)} />{labels.legendBusy}</span>
        <span className="flex items-center gap-1"><span className={cn('size-3 rounded-sm', KIND_CLASS.tentative)} />{labels.legendTentative}</span>
        <span className="flex items-center gap-1"><span className={cn('size-3 rounded-sm', KIND_CLASS.oof)} />{labels.legendAway}</span>
        <span className="flex items-center gap-1"><span className="size-3 rounded-sm border-x-2 border-ring bg-ring/10" />{labels.slot}</span>
      </div>
    </section>
  );
}
